import { useTranslation } from 'react-i18next';
import {
  AccountCard,
  AccountListHeader,
  type AccountCardAction,
} from '@penta/penta-shared';
import type { DashboardView } from '../hooks/useDashboardViewPreference';

/**
 * Sjednocený tvar položky seznamu — API vrací pro každý typ produktu jinou
 * strukturu (běžný účet má disponibilní zůstatek, termínovaný vklad ne), takže
 * se rozdíly řeší při mapování v dashboardu a sem přichází už jen to, co se
 * vykresluje.
 */
export type AccountListItem = {
  id: number;
  /** Titulek řádku/karty — název produktu, u účtů všech firem název firmy. */
  title: string;
  /** Číslo účtu nebo smlouvy. */
  description: string;
  /**
   * Disponibilní zůstatek. Chybí u produktů, kde ho API nevrací (termínované
   * vklady, úvěry) — tam se nesmí místo něj podstrčit účetní zůstatek, jinak
   * uživatel čte jako disponibilní něco, co jím není.
   */
  availableBalance?: number;
  /** Účetní zůstatek. */
  bookBalance: number;
  currency: string;
};

type AccountListProps = {
  items: AccountListItem[];
  variant: DashboardView;
  /** Prefix pro `data-testid` jednotlivých položek. */
  testIdPrefix: string;
  /** Akce karty ve standardním zobrazení; kompaktní řádky akce nemají. */
  getActions?: (id: number) => AccountCardAction[];
  /** Popisek druhého sloupce — u vkladů „Číslo smlouvy" místo čísla účtu. */
  descriptionLabel?: string;
  formatCurrency: (amount: number, currency: string) => string;
};

/**
 * Seznam produktů na dashboardu ve standardním (karty) nebo kompaktním
 * (tabulkové řádky) zobrazení.
 */
export function AccountList({
  items,
  variant,
  testIdPrefix,
  getActions,
  descriptionLabel,
  formatCurrency,
}: AccountListProps) {
  const { t } = useTranslation();

  if (variant === 'compact') {
    return (
      <>
        <AccountListHeader
          titleLabel={t('dashboard.columns.accountName')}
          descriptionLabel={
            descriptionLabel ?? t('dashboard.columns.accountNumber')
          }
          secondaryLabel={t('dashboard.columns.bookBalance')}
          amountLabel={t('dashboard.columns.availableBalance')}
          data-testid={`${testIdPrefix}-header`}
        />
        {items.map((item) => (
          <AccountCard
            key={item.id}
            variant="compact"
            data-testid={`${testIdPrefix}-${item.id}`}
            title={item.title}
            description={item.description}
            secondaryAmount={formatCurrency(item.bookBalance, item.currency)}
            secondaryLabel={t('dashboard.columns.bookBalance')}
            amount={
              item.availableBalance === undefined
                ? '—'
                : formatCurrency(item.availableBalance, item.currency)
            }
            unit=""
          />
        ))}
      </>
    );
  }

  return (
    <>
      {items.map((item) => (
        <AccountCard
          key={item.id}
          data-testid={`${testIdPrefix}-${item.id}`}
          title={item.title}
          description={item.description}
          amount={formatCurrency(
            item.availableBalance ?? item.bookBalance,
            item.currency,
          )}
          unit=""
          subvalue={
            // Bez disponibilního zůstatku je hlavní částka rovnou účetní —
            // opakovat ji v podtitulku by ukázalo dvakrát totéž číslo.
            item.availableBalance === undefined
              ? undefined
              : `${t('accounts.accountBalance')}: ${formatCurrency(
                  item.bookBalance,
                  item.currency,
                )}`
          }
          actions={getActions?.(item.id)}
        />
      ))}
    </>
  );
}
